<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <!-- Top bar -->
        <div class="page-bar py-3">
          <div class="container">
            <div class="d-flex align-items-center">
              <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                <i class="fas fa-chevron-left"></i>
              </BackButton>
              <div class="page-title flex-grow-1">Hired Vendors</div>
            </div>
          </div>
        </div>

        <!-- Helper box -->
        <div class="container mt-3 mb-5 shadow-sm bg-white py-3 rounded">
          <p class="text-dark mb-2">Vendors who are hired for this event will appear here.</p>
          <div class="mt-2">
            <router-link :to="`/detail/vendors/add-vendor/${eventId}`" class="btn btn-outline-success btn-sm">
              <i class="fas fa-plus mr-1"></i> Add Vendors
            </router-link>
          </div>
        </div>

        <!-- Vendors list -->
        <div v-if="!loading && vendors.length > 0" class="container mt-3 mb-5 shadow-sm bg-white py-3 rounded">
          <div class="card vendor-card shadow-sm mb-2" v-for="v in vendors" :key="v.id">
            <div class="card-body py-3">
              <div class="d-flex align-items-start">
                <!-- Logo -->
                <img v-no-right-click class="vendor-logo mr-3" :src="v.logo || fallbackLogo" alt="Logo" />

                <!-- Main -->
                <div class="flex-grow-1 min-w-0">
                  <div class="d-flex align-items-start">
                    <h5 class="vendor-title mr-auto mb-2 text-truncate" :title="v.name">
                      {{ v.name || '—' }}
                    </h5>

                    <div class="dropdown ml-2">
                      <button class="btn btn-link p-0 text-dark more-btn" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <i class="fas fa-ellipsis-v"></i>
                      </button>
                      <div class="dropdown-menu dropdown-menu-right">
                        <a class="dropdown-item" href="javascript:void(0)" @click.prevent="openUpdateModal(v.raw)">
                          <i class="far fa-edit mr-2"></i>Edit Hired Vendor
                        </a>
                        <div class="dropdown-divider"></div>
                        <a class="dropdown-item text-danger" href="javascript:void(0)" @click.prevent="openDeleteModal(v.raw)">
                          <i class="far fa-trash-alt mr-2"></i>Delete Vendor
                        </a>
                      </div>
                    </div>
                  </div>

                  <div class="media-row" v-if="v.phone">
                    <span class="icon-badge mr-2"><i class="fas fa-phone"></i></span>
                    <a :href="'tel:' + telHref(v.phone)" class="vendor-link">{{ v.phone }}</a>
                  </div>

                  <div class="media-row" v-if="v.email">
                    <span class="icon-badge mr-2"><i class="far fa-envelope"></i></span>
                    <a :href="'mailto:' + v.email" class="vendor-link text-truncate d-inline-block w-100 w-sm-auto">
                      {{ v.email }}
                    </a>
                  </div>

                  <div class="vendor-tags text-muted" v-if="v.categories">
                    {{ v.categories }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="container mt-3 mb-5 shadow-sm bg-white py-3 rounded text-center" v-else>
           <p class="py-2">There are No Vendors Available for this event. You can Add Vendors, Select Add Vendors</p>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center p-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <div class="mt-2 text-muted small">Fetching hired vendors…</div>
        </div>
      </div>
    </div>

    <!-- UPDATE CATEGORIES MODAL -->
    <div v-if="showModal">
      <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="editModalTitle">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 id="editModalTitle" class="modal-title">Edit Hired Vendors</h5>
              <button type="button" class="close" aria-label="Close" @click="closeModal">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div class="modal-body">
              <p class="mb-2">The Following Guests are invited to this event. Please Select the individuals who will be allowed to upload photos to this album,</p>
              <div v-if="editVendor" class="mb-3">
                  <div class="d-flex align-items-center">
                      <div><img v-no-right-click :src="`${editVendor.s_user_img}`" alt="" class="img-fluid avatar avatar-sm rounded"></div>
                      <div><strong class="h5 mb-0">{{ editVendor.s_business || 'Vendor' }}</strong></div>
                  </div>
              </div>

              <div v-if="categoryOptions.length" class="categories-list">
                <div class="form-check mb-2" v-for="opt in categoryOptions" :key="opt.id">
                  <input class="form-check-input" type="checkbox" :value="opt.id" :id="'opt-' + opt.id" v-model="selectedIds" />
                  <label class="form-check-label" :for="'opt-' + opt.id">{{ opt.name }}</label>
                </div>
              </div>
              <div v-else class="text-muted small">No categories available for this vendor.</div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
              <button type="button" class="btn btn-primary" @click="updateCategories" :disabled="saving || selectedIds.length === 0">
                <span v-if="saving" class="spinner-border spinner-border-sm mr-2"></span>
                {{ saving ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" @click="closeModal"></div>
    </div>

    <!-- DELETE CONFIRM MODAL -->
    <div v-if="showDelete">
      <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="deleteModalTitle">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 id="deleteModalTitle" class="modal-title">Delete Hired Vendor</h5>
              <button type="button" class="close" aria-label="Close" @click="closeDeleteModal">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div class="modal-body">
              <p class="mb-2">
                Are you sure you want to remove
                <strong>{{ deleteVendorRow?.s_business || 'this vendor' }}</strong>
                from this event?
              </p>
              <p class="text-muted small mb-0">This will unlink the vendor from the event.</p>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeDeleteModal">Cancel</button>
              <button type="button" class="btn btn-danger" @click="deleteVendor" :disabled="deleting">
                <span v-if="deleting" class="spinner-border spinner-border-sm mr-2"></span>
                {{ deleting ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" @click="closeDeleteModal"></div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import api from "@/services/api";
import { useSessionStore } from "@/stores/session";
import BackButton from '@/components/common/BackButton.vue';

export default {
  name: "HiredVendors",
  components: { BackButton },
  setup() {
    const route = useRoute();
    const session = useSessionStore();

    const deriveEventId = (r) => {
      const cand = [
        r.params?.event_id, r.params?.id,
        r.query?.event_id,  r.query?.id,
      ].find(v => v != null && String(v).trim() !== "");
      if (cand) return String(cand);
      const lastSeg = (r.path || r.fullPath || "")
        .split("?")[0].split("/").filter(Boolean).pop();
      return lastSeg && /^\d+$/.test(lastSeg) ? lastSeg : null;
    };

    const eventId = ref(deriveEventId(route));
    watch(() => route.fullPath, async () => {
      const next = deriveEventId(route);
      if (next !== eventId.value) {
        eventId.value = next;
        await loadVendors();
      }
    });

    const loading = ref(true);
    const vendors = ref([]);
    const fallbackLogo = "https://dummyimage.com/72x72/f3f4f6/9ca3af.png&text=V";

    const telHref = (raw) => String(raw || "").replace(/\s+/g, "");

    async function loadVendors() {
      try {
        loading.value = true;
        if (!eventId.value) { vendors.value = []; return; }

        const fd = new FormData();
        fd.append("userid", session.userId);
        fd.append("event_id", eventId.value);

        const res = await api.getHiredVendors(fd);
        const payload = res?.data ?? res;
        if (payload?.status !== "ok") { vendors.value = []; return; }

        const list = Array.isArray(payload.list) ? payload.list : [];
        vendors.value = list.map((v, i) => ({
          id: v.invite_id || v.s_id || `v-${i}`,
          name: v.s_business || "Vendor",
          logo: v.s_user_img || "",
          phone: v.s_phone || "",
          email: v.s_email || "",
          categories: v.selected_context_categories || v.context_categories || "",
          raw: v, // keep all fields for editing/deleting
        }));
      } catch (e) {
        console.error("Error fetching hired vendors:", e);
        vendors.value = [];
      } finally {
        loading.value = false;
      }
    }

    // ---------- UPDATE CATEGORIES MODAL ----------
    const showModal = ref(false);
    const editVendor = ref(null);
    const categoryOptions = ref([]);
    const selectedIds = ref([]);
    const saving = ref(false);

    const buildOptions = (idsCsv = "", namesCsv = "") => {
      const ids = String(idsCsv).split(",").map(s => s.trim()).filter(Boolean);
      const names = String(namesCsv).split(",").map(s => s.trim()).filter(Boolean);
      const opts = [];
      const n = Math.max(ids.length, names.length);
      for (let i = 0; i < n; i++) {
        opts.push({ id: ids[i] || `cat-${i}`, name: names[i] || names[0] || "Category" });
      }
      const seen = new Set();
      return opts.filter(o => (seen.has(o.id) ? false : (seen.add(o.id), true)));
    };

    const openUpdateModal = (row) => {
      editVendor.value = row;
      categoryOptions.value = buildOptions(row.context_categories_ids, row.context_categories);
      selectedIds.value = String(row.selected_context_categories_ids || "")
        .split(",").map(s => s.trim()).filter(Boolean);
      showModal.value = true;
    };
    const closeModal = () => {
      showModal.value = false;
      editVendor.value = null;
      categoryOptions.value = [];
      selectedIds.value = [];
    };
    const updateCategories = async () => {
      if (!editVendor.value || selectedIds.value.length === 0) return;
      try {
        saving.value = true;
        const fd = new FormData();
        fd.append("invite_id", editVendor.value.invite_id);
        fd.append("categories", selectedIds.value.join(","));
        const res = await api.updateHiredVendors(fd);
        const payload = res?.data ?? res;
        if (payload?.status === "ok" || payload?.status === true) {
          await loadVendors();
          closeModal();
        } else {
          throw new Error(payload?.message || "Failed to update vendor categories");
        }
      } catch (e) {
        console.error("Error updating categories:", e);
        alert("Failed to update vendor categories. Please try again.");
      } finally {
        saving.value = false;
      }
    };

    // ---------- DELETE HIRED VENDOR MODAL ----------
    const showDelete = ref(false);
    const deleteVendorRow = ref(null);
    const deleting = ref(false);

    const openDeleteModal = (row) => {
      deleteVendorRow.value = row; // row contains s_id (vendor_id) + invite_id + event context
      showDelete.value = true;
    };
    const closeDeleteModal = () => {
      showDelete.value = false;
      deleteVendorRow.value = null;
      deleting.value = false;
    };
    const deleteVendor = async () => {
      if (!deleteVendorRow.value) return;
      try {
        deleting.value = true;

        const fd = new FormData();
        fd.append("user_id", session.userId);
        fd.append("vendor_id", deleteVendorRow.value.s_id); // required
        fd.append("event_id", eventId.value);               // required

        const res = await api.deleteHiredVendors(fd);
        const payload = res?.data ?? res;
        if (payload?.status === "ok" || payload?.status === true) {
          await loadVendors();
          closeDeleteModal();
        } else {
          throw new Error(payload?.message || "Failed to delete hired vendor");
        }
      } catch (e) {
        console.error("Error deleting hired vendor:", e);
        alert("Failed to delete vendor. Please try again.");
      } finally {
        deleting.value = false;
      }
    };

    onMounted(loadVendors);

    return {
      eventId,
      loading,
      vendors,
      fallbackLogo,
      telHref,

      // update modal
      showModal,
      editVendor,
      categoryOptions,
      selectedIds,
      saving,
      openUpdateModal,
      closeModal,
      updateCategories,

      // delete modal
      showDelete,
      deleteVendorRow,
      deleting,
      openDeleteModal,
      closeDeleteModal,
      deleteVendor,
    };
  },
};
</script>

<style scoped>
.page-bar{
  position: sticky;
  top: 0;
  z-index: 1000;
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.page-title{ font-weight: 600; font-size: 1.05rem; }

/* card styles */
.vendor-card { border: 0; border-radius: .75rem; }
.vendor-logo  { width: 48px; height: 48px; border-radius: 8px; object-fit: cover; }
.vendor-title { font-weight: 700; line-height: 1.2; font-size: 1.125rem; }
.more-btn     { border: 0; background: transparent; font-size: 18px; line-height: 1; }
.media-row    { display:flex; align-items:center; margin-bottom:.35rem; min-width:0; }
.icon-badge{
  display:inline-flex; align-items:center; justify-content:center;
  width:28px; height:28px; min-width:28px; border-radius:8px;
  background:#FB721030; color:#2f6bd9; font-size:14px;
}
.vendor-link  { color:#1d1d1f; font-size:1rem; }
.vendor-tags  { margin-top:.15rem; font-size:1rem; color:#6c757d; }

/* modal helpers */
.categories-list { max-height: 300px; overflow-y: auto; }
.modal.d-block { display: block; }
.modal-backdrop { position: fixed; top:0; left:0; right:0; bottom:0; background: rgba(0,0,0,.5); }

/* responsiveness */
@media (min-width: 576px){ .vendor-title{ font-size:1.25rem; } }
@media (min-width: 768px){ .vendor-logo{ width:56px; height:56px; } }
</style>
